import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  FormControl,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { commonInlineStyles, inputFieldStyles } from "../../styles";

const useStyles = makeStyles((theme) => ({
  formContainer: { padding: "24px" },
  formTitle: { marginBottom: theme.spacing(4), fontWeight: 500 },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  },
  button: { minWidth: 180, fontWeight: 600, fontSize: 16 },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "14px",
    marginTop: theme.spacing(0.5),
  },
  label: { marginBottom: theme.spacing(1), fontWeight: 500 },
  readOnlyValue: {
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
}));

const validateAdminFullName = (value) => /^[a-zA-Z\s]*$/.test(value);
const validateAdminEmail = (value) =>
  /^[a-zA-Z0-9._%+\-@]*$/.test(value) &&
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
const validatePhone = (value) => /^\+?[0-9\s-]{7,20}$/.test(value);

const AdminDetailsSection = forwardRef(
  (
    {
      initialData = {},
      onSave,
      onChange,
      onEditModeChange,
      disableEdit = false,
      editMode: initialEditMode = false,
      onDiscard,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      saasUserId,
    },
    ref
  ) => {
    const classes = useStyles();
    const [form, setForm] = useState({ ...initialData });
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(initialEditMode);
    // Use ref to preserve editMode state across renders
    const editModeRef = useRef(false);
    // Email edit dialog state
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [pendingEmail, setPendingEmail] = useState("");
    const [emailDialogError, setEmailDialogError] = useState("");
    // Phone code state - initialize with initialData.phoneNumber if available
    const [phoneCode, setPhoneCode] = useState(() => {
      if (initialData.phoneNumber) {
        return initialData.phoneNumber.startsWith("+")
          ? initialData.phoneNumber
          : `+${initialData.phoneNumber}`;
      }
      return "";
    });
    // Track if component is mounted
    const isMounted = useRef(false);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => form,
      getPayload: () => createPayload(),
      validateForm: () => validateForm(),
      resetForm: () => {
        setForm({ ...initialData });
        setErrors({});
      },
    }));

    // Set mounted flag on initial render
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    // Track editMode changes and sync with ref
    useEffect(() => {
      editModeRef.current = editMode;

      // Skip initial render
      if (!isMounted.current) return;

      // Skip if change came from prop
      if (editMode === initialEditMode) return;

      // Notify parent when edit mode changes
      if (onEditModeChange) {
        onEditModeChange(editMode);
      }
    }, [editMode, onEditModeChange, initialEditMode]);

    // Handle initialEditMode prop changes
    useEffect(() => {
      if (initialEditMode !== editMode) {
        setEditMode(initialEditMode);
      }
    }, [initialEditMode]);

    // Ensure phoneCode stays synchronized with form.phoneNumber
    useEffect(() => {
      if (form.phoneNumber && (!phoneCode || phoneCode !== form.phoneNumber)) {
        const formattedPhone = form.phoneNumber.startsWith("+")
          ? form.phoneNumber
          : `+${form.phoneNumber}`;
        setPhoneCode(formattedPhone);
      }
    }, [form.phoneNumber, phoneCode]);

    useEffect(() => {
      // Only update form if initialData actually changed in content, not just reference
      const hasContentChanged =
        JSON.stringify(form) !== JSON.stringify(initialData);
      if (hasContentChanged) {
        setForm({ ...initialData });
        setErrors({});

        // Extract phone code if available
        if (initialData.phoneNumber) {
          // If the phone number starts with a plus, use it as is
          if (initialData.phoneNumber.startsWith("+")) {
            setPhoneCode(initialData.phoneNumber);
          } else {
            // Otherwise, add a plus
            setPhoneCode(`+${initialData.phoneNumber}`);
          }
        } else {
          setPhoneCode(""); // Reset if no phone number
        }
      }
    }, [initialData]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      const updatedForm = { ...form, [name]: value };
      setForm(updatedForm);
      if (onChange) onChange(updatedForm);

      // Clear error for this field when user types
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    };

    const handlePhoneChange = (value, country, e, formattedValue) => {
      // Ensure value has a + prefix
      const phoneWithPrefix = value.startsWith("+") ? value : `+${value}`;
      setPhoneCode(phoneWithPrefix);
      const updatedForm = { ...form, phoneNumber: phoneWithPrefix };
      setForm(updatedForm);
      if (onChange) onChange(updatedForm);

      // Clear error for phoneNumber when user types
      if (errors.phoneNumber) {
        setErrors((prev) => ({ ...prev, phoneNumber: "" }));
      }
    };



    // Email edit dialog handlers
    const handleEmailEditClick = () => {
      setPendingEmail(form.emailAddress || "");
      setEmailDialogError("");
      setEmailDialogOpen(true);
    };
    const handleEmailDialogCancel = () => {
      setEmailDialogOpen(false);
      setEmailDialogError("");
    };
    const handleEmailDialogConfirm = () => {
      if (!pendingEmail || !validateAdminEmail(pendingEmail)) {
        setEmailDialogError("Invalid Email");
        return;
      }
      const updatedForm = { ...form, emailAddress: pendingEmail };
      setForm(updatedForm);
      if (onChange) onChange(updatedForm);
      setEmailDialogOpen(false);
      setEmailDialogError("");
    };

    const validate = () => {
      let valid = true;
      let errs = {};

      if (!form.fullName || !validateAdminFullName(form.fullName)) {
        errs.fullName = "Invalid Name";
        valid = false;
      }
      if (!form.emailAddress || !validateAdminEmail(form.emailAddress)) {
        errs.emailAddress = "Invalid Email";
        valid = false;
      }
      if (!form.phoneNumber || !form.phoneNumber.trim()) {
        errs.phoneNumber = "Phone number is required";
        valid = false;
      } else if (!validatePhone(form.phoneNumber)) {
        errs.phoneNumber = "Invalid phone number";
        valid = false;
      }

      setErrors(errs);
      return valid;
    };

    // Renamed to match the expected method name from useImperativeHandle
    const validateForm = () => {
      return validate();
    };

    // Create a payload for the API
    const createPayload = () => {
      return {
        vendorMosymphonyID,
        brandOwnerMosymphonyID,
        saasUserId: saasUserId,
        ecosystemID: Number(localStorage.getItem("ecosystemId")),
        cmoAdminDetails: {
          fullName: form.fullName,
          phoneNumber: form.phoneNumber,
          emailAddress: form.emailAddress,
        },
        updateCmoAdminDetailsFlag: true,
      };
    };



    return (
      <div className="cmo-stepper-wrapper cmo-details-wrapper">
        <div className="stepper-container">
          <form autoComplete="off">
            <Grid className="two-col-forms-grid" container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography component="label" htmlFor="fullName">
                  Full Name {editMode && <span style={{ color: "red" }}>*</span>}
                </Typography>
                {!editMode ? (
                  <Typography>{form.fullName || ""}</Typography>
                ) : (
                  <FormControl className={classes.formField}>
                    <TextField
                      id="fullName"
                      name="fullName"
                      value={form.fullName || ""}
                      onChange={handleChange}
                      onBlur={(e) => {
                        const { name, value } = e.target;
                        // Trim leading and trailing spaces for string values
                        if (typeof value === "string" && value.trim() !== value) {
                          handleChange({ target: { name, value: value.trim() } });
                        }
                      }}
                      variant="outlined"
                      placeholder="Enter text here"
                      fullWidth
                      required
                      error={!!errors.fullName}
                      helperText={errors.fullName || ""}
                      InputProps={{ style: commonInlineStyles?.input }}
                      sx={inputFieldStyles}
                      inputProps={{
                        ...commonInlineStyles?.inputProps,
                        maxLength: 30,
                      }}
                      style={commonInlineStyles?.input}
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .replace(/[^a-zA-Z\s]/g, "")
                          .replace(/(\s{2,})/g, (match) => match[0]);
                      }}
                    />
                  </FormControl>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              // style={{ display: "flex", alignItems: "flex-end" }}
              >
                <Typography component="label" htmlFor="emailAddress">
                  Email Address {editMode && <span style={{ color: "red" }}>*</span>}
                  {/* <IconButton
                size="small"
                style={{ marginLeft: 8 }}
                onClick={handleEmailEditClick}
              >
                <EditIcon fontSize="small" />
              </IconButton> */}
                </Typography>
                {/* {!editMode ? ( */}
                <Typography>{form.emailAddress || ""}</Typography>
                {/* ) : (
              <FormControl
                className={classes.formField}
                style={{ width: "100%" }}
              >
                <TextField
                  id="emailAddress"
                  name="emailAddress"
                  value={form.emailAddress || ""}
                  variant="outlined"
                  placeholder="Enter email here"
                  fullWidth
                  required
                  error={!!errors.emailAddress}
                  helperText={errors.emailAddress || ""}
                  inputProps={{
                    maxLength: 100,
                    readOnly: true,
                  }}
                />
              </FormControl>
            )} */}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography component="label" htmlFor="phoneNumber">
                  Phone Number {editMode && <span style={{ color: "red" }}>*</span>}
                </Typography>
                {!editMode ? (
                  <Typography>{form.phoneNumber || ""}</Typography>
                ) : (
                  <FormControl className={classes.formField}>
                    <PhoneInput
                      country={"us"}
                      value={phoneCode || form.phoneNumber || ""}
                      onChange={handlePhoneChange}
                      enableSearch={true}
                      defaultErrorMessage="Invalid phone number"
                      inputStyle={{
                        width: "80%",
                        height: "2.5rem",
                        // borderColor:
                        //   submitted && Boolean(errors.adminPhone)
                        //     ? "#f44336"
                        //     : "#0000008a",
                      }}
                      containerStyle={{ margin: "0" }}
                      dropdownStyle={{ borderColor: "#0000008a" }}
                      countryCodeEditable={false}
                      isValid={!errors.phoneNumber}
                      specialLabel=""
                    />
                    {errors?.phoneNumber && (
                      <Typography
                        variant="caption"
                        style={{
                          color: "#f44336",
                          fontSize: "14px",
                          display: "block",
                          marginTop: "4px",
                        }}
                      >
                        {errors?.phoneNumber}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>
            </Grid>
            {/* Buttons removed - edit, save and discard are now handled by parent component */}
            <Dialog
              open={emailDialogOpen}
              onClose={handleEmailDialogCancel}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle
                style={{
                  fontWeight: 700,
                  fontSize: 24,
                  textAlign: "center",
                  marginTop: 16,
                }}
              >
                Are you sure you want to change the Admin Email?
              </DialogTitle>
              <DialogContent
                style={{ textAlign: "center", fontSize: 18, marginBottom: 8 }}
              >
                This action will deactivate the Current Email Address and a
                login invite will be sent to the New Email Address
                <Grid container spacing={2} style={{ marginTop: 24 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
                      Current Email Address
                    </Typography>
                    <div
                      style={{
                        padding: 8,
                        background: "#f5f5f5",
                        borderRadius: 4,
                      }}
                    >
                      {form.emailAddress}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
                      Enter New Email Address
                    </Typography>
                    <TextField
                      value={pendingEmail}
                      onChange={(e) => setPendingEmail(e.target.value)}
                      onBlur={(e) => {
                        const { name, value } = e.target;
                        // Trim leading and trailing spaces for string values
                        if (typeof value === "string" && value.trim() !== value) {
                          setPendingEmail(value.trim());
                        }
                      }}
                      variant="outlined"
                      placeholder="Enter text here"
                      fullWidth
                      error={!!emailDialogError}
                      helperText={emailDialogError}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions
                style={{ justifyContent: "center", marginBottom: 16 }}
              >
                <Button
                  onClick={handleEmailDialogCancel}
                  variant="outlined"
                  style={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEmailDialogConfirm}
                  variant="contained"
                  color="primary"
                  style={{ minWidth: 180, background: "#ec6535" }}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </form>
          <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />

        </div>
      </div>
    );
  }
);

export default AdminDetailsSection;
